import * as RadixDialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import { forwardRef, HTMLAttributes } from "react";
import classes from "./sheet.module.css";

type TriggerProps = React.ComponentPropsWithoutRef<
  typeof RadixDialog.Trigger
> & {
  mute?: boolean;
};

export const Trigger = forwardRef<
  React.ElementRef<typeof RadixDialog.Trigger>,
  TriggerProps
>(({ mute = false, onClick, ...props }, ref) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (mute) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  return <RadixDialog.Trigger ref={ref} onClick={handleClick} {...props} />;
});
export const Close = RadixDialog.Close;

export const Title = RadixDialog.Title;

export const Description = RadixDialog.Description;

export type RootProps = HTMLAttributes<HTMLElement> & {
  allowMultipleInstances?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  modal?: boolean;
};

export const Root = ({
  onOpenChange,
  modal = true,
  ...props
}: RootProps): React.ReactElement => {
  return (
    <RadixDialog.Root {...props} onOpenChange={onOpenChange} modal={modal} />
  );
};

Root.displayName = "SheetRoot";

export type OverlayProps = HTMLAttributes<HTMLElement> & {
  overlayAnimation: boolean;
};

export const Overlay = forwardRef<
  React.ElementRef<typeof RadixDialog.Overlay>,
  OverlayProps
>(({ overlayAnimation, className, ...props }, ref): React.ReactElement => {
  return (
    <RadixDialog.Overlay
      data-testid="sheet-overlay"
      ref={ref as React.LegacyRef<HTMLDivElement>}
      className={clsx(classes.overlay, className, {
        [classes.fadeIn as string]: overlayAnimation,
        [classes.fadeOut as string]: overlayAnimation,
      })}
      {...props}
    />
  );
});

Overlay.displayName = "Overlay";

export type ContentProps = HTMLAttributes<HTMLElement> & {
  position: "top" | "right" | "bottom" | "left" | "center";
  overlayAnimation: boolean;
  showOverlay?: boolean;
  forceMount?: true;
};

export const Content = forwardRef<
  React.ElementRef<typeof RadixDialog.Content>,
  ContentProps
>(
  (
    {
      position,
      overlayAnimation,
      className,
      children,
      showOverlay = true,
      forceMount = undefined,
      ...props
    },
    ref,
  ) => {
    return (
      <RadixDialog.Portal forceMount={forceMount}>
        {showOverlay && <Overlay overlayAnimation={overlayAnimation} />}
        <RadixDialog.Content
          data-testid="sheet-content"
          ref={ref}
          className={clsx(classes.content, className, classes[position])}
          {...props}
        >
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    );
  },
);
Content.displayName = "Content";

export const Header = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={clsx([classes.header, className])} {...props}>
      {children}
    </div>
  );
});
Header.displayName = "Header";

export const Main = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx(classes.main, className)} {...props}>
        {children}
      </div>
    );
  },
);
Main.displayName = "Main";

export const Footer = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={clsx(classes.footer, className)} {...props}>
      {children}
    </div>
  );
});
Footer.displayName = "Footer";
